// The diorama: renderer, camera rig, sky with the real moon phase, the night
// sea, the Blender-built shelf and village, and lights taken from its empties.
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { Post } from './post.js';
import { BASINS } from '../sim/basins.js';

export const PITCH = THREE.MathUtils.degToRad(55);
export const FOV = 26;

const skyVS = /* glsl */`varying vec3 vDir; void main() { vDir = normalize(position); gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); gl_Position.z = gl_Position.w; }`;
const skyFS = /* glsl */`
uniform vec3 top; uniform vec3 horizon; uniform vec3 moonDir; uniform float moonLight; uniform float phase; uniform float time; uniform float moonSize;
varying vec3 vDir;
float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
void main() {
  vec3 d = normalize(vDir);
  float h = clamp(d.y, -0.2, 1.0);
  vec3 c = mix(horizon, top, smoothstep(-0.02, 0.55, h));
  // Pixel stars, fewer under a bright moon.
  vec2 sp = floor(vec2(atan(d.z, d.x) * 520.0, d.y * 520.0));
  float s = step(0.9965 + moonLight * 0.002, hash(sp)) * smoothstep(0.02, 0.2, h);
  c += vec3(0.85, 0.9, 1.0) * s * (0.5 + 0.5 * sin(time * 2.0 + hash(sp) * 40.0));
  // Moon disc with the night's phase, and its halo.
  vec3 m = normalize(moonDir);
  float cosA = dot(d, m);
  float r = 0.026 * moonSize;
  vec3 right = normalize(cross(vec3(0, 1, 0), m)), up = cross(m, right);
  vec2 p = vec2(dot(d, right), dot(d, up)) / r;
  float disc = step(length(p), 1.0) * step(0.0, cosA);
  // Terminator: phase 0 new .. 0.5 full .. 1 new (waxing lit on the right).
  float k = cos(phase * 6.2831853);
  float x = p.x, edge = sqrt(max(0.0, 1.0 - p.y * p.y));
  float litSide = phase < 0.5 ? step(k * edge, x) : step(x, -k * edge);
  vec3 moonCol = vec3(0.96, 0.95, 0.86) * (0.12 + 0.88 * litSide);
  moonCol *= 0.9 + 0.1 * floor(hash(floor(p * 6.0)) * 3.0) / 3.0;
  // Kagee (bigger disc) keeps the moon under the bloom threshold so its phase reads.
  float hot = moonSize > 1.5 ? 0.95 : 1.6;
  c = mix(c, moonCol * hot, disc);
  float halo = (pow(max(cosA, 0.0), 900.0) * 0.6 + pow(max(cosA, 0.0), 60.0) * 0.12) * (moonSize > 1.5 ? 0.35 : 1.0);
  c += vec3(0.6, 0.7, 0.9) * halo * moonLight;
  gl_FragColor = vec4(c, 1.0);
}`;

const seaVS = /* glsl */`varying vec3 vWorld; void main() { vec4 w = modelMatrix * vec4(position, 1.0); vWorld = w.xyz; gl_Position = projectionMatrix * viewMatrix * w; }`;
const seaFS = /* glsl */`
uniform float time; uniform vec3 moonDir; uniform float moonLight; uniform vec3 camPos; uniform float tide; uniform float clarity; uniform float kagee;
varying vec3 vWorld;
float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float noise(vec2 p) { vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1, 0)), f.x), mix(hash(i + vec2(0, 1)), hash(i + vec2(1, 1)), f.x), f.y); }
// Swells as a few Gerstner waves (Finch, GPU Gems 2004), used for shading only:
// adds each wave's slope to the normal and returns crest height in .w.
vec4 gerstner(vec2 p, float t) {
  vec3 n = vec3(0.0, 1.0, 0.0); float h = 0.0;
  vec4 W[4];
  W[0] = vec4(normalize(vec2(0.25, 1.0)), 13.0, 0.20);   // dir, wavelength (units), amplitude
  W[1] = vec4(normalize(vec2(-0.4, 1.0)), 7.5, 0.11);
  W[2] = vec4(normalize(vec2(0.9, 0.6)), 4.2, 0.05);
  W[3] = vec4(normalize(vec2(-0.1, 1.0)), 21.0, 0.28);
  for (int i = 0; i < 4; i++) {
    float k = 6.2832 / W[i].z, c = sqrt(9.8 / k);
    float f = k * dot(W[i].xy, p) - c * k * t * 0.35;
    float a = W[i].w;
    n.xz -= W[i].xy * k * a * cos(f);
    n.y -= 0.6 * k * a * sin(f);
    h += a * sin(f);
  }
  return vec4(normalize(n), h);
}
void main() {
  vec2 wp = floor(vWorld.xz * 16.0) / 16.0;
  vec4 gw = gerstner(vWorld.xz, time);
  float w = noise(wp * vec2(0.18, 0.5) + vec2(time * 0.05, time * 0.12)) * 0.6 + noise(wp * 0.9 + time * 0.2) * 0.4;
  w = mix(w, clamp(0.5 + gw.w * 1.1, 0.0, 1.0), 0.55);   // the swells show as bands of light and dark
  vec3 deep = vec3(0.02, 0.05, 0.12), lift = vec3(0.06, 0.13, 0.24);
  vec3 c = mix(deep, lift, w * 0.8);
  // Moon path: a column of glints toward the camera.
  vec3 v = normalize(camPos - vWorld);
  vec3 m = normalize(moonDir);
  vec3 hdir = normalize(v + m);
  vec3 sn = normalize(gw.xyz + vec3((noise(wp * 1.3 - time * 0.3) - 0.5) * 0.3, 0.0, (w - 0.5) * 0.3));
  float spec = pow(max(dot(hdir, sn), 0.0), 260.0);
  float glint = step(0.55, hash(floor(vWorld.xz * 16.0) + floor(time * 4.0)));
  c += vec3(0.9, 0.92, 0.85) * min(spec * (0.6 + glint), 1.0) * moonLight * 0.9;
  float dist = length(vWorld.xz - camPos.xz);
  c = mix(c, vec3(0.05, 0.08, 0.17), smoothstep(60.0, 260.0, dist));
  if (kagee > 0.5) {
    // Backlit sea: a luminous indigo sheet with strong swells (the waves must
    // read from the title shot) and the moon road glinting on it.
    vec3 k = mix(vec3(0.002, 0.008, 0.05), vec3(0.016, 0.05, 0.22), w);
    k += vec3(0.95, 0.95, 0.85) * min(spec * (0.6 + glint), 1.0) * (0.4 + moonLight);
    k = mix(k, vec3(0.008, 0.03, 0.15), smoothstep(60.0, 260.0, dist));
    c = k;
  }
  // Near the shelf the incoming tide is clear enough to see lit pools beneath.
  float a = 1.0 - clarity * (1.0 - smoothstep(30.0, 90.0, dist)) * 0.55;
  gl_FragColor = vec4(c, a);
}`;

// Calm water in every basin, so the whole shelf reads as tide pools. The
// active pool draws its own interactive surface on top; finished pools keep a
// lagoon glow in proportion to how many little ones were saved.
const basinVS = /* glsl */`varying vec3 vWorld; void main() { vec4 w = modelMatrix * vec4(position, 1.0); vWorld = w.xyz; gl_Position = projectionMatrix * viewMatrix * w; }`;
const basinFS = /* glsl */`
uniform float time; uniform float lit; uniform float moon; uniform float kagee;
varying vec3 vWorld;
float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float noise(vec2 p) { vec2 i = floor(p), f = fract(p); f = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1, 0)), f.x), mix(hash(i + vec2(0, 1)), hash(i + vec2(1, 1)), f.x), f.y); }
void main() {
  vec2 wp = floor(vWorld.xz * 32.0) / 32.0;
  float caust = noise(wp * 1.7 + vec2(time * 0.21, -time * 0.17)) * noise(wp * 2.3 - vec2(time * 0.13, time * 0.19));
  vec3 c = mix(vec3(0.006, 0.016, 0.04), vec3(0.025, 0.06, 0.11), 0.35 + caust * (0.3 + moon * 0.5));
  vec3 lagoon = mix(vec3(0.05, 0.24, 0.30), vec3(0.16, 0.46, 0.50), caust);
  c = mix(c, lagoon, lit);
  float tw = step(0.985, hash(floor(wp * 32.0) + floor(time * 2.5)));
  c += vec3(0.5, 1.0, 1.1) * tw * (0.08 * moon + 0.6 * lit);
  c += vec3(0.1, 0.5, 0.6) * lit * lit * 0.6;
  if (kagee > 0.5) {
    // Other pools are dim screens; finished ones keep a warm glow behind the paper.
    float ck = noise(vWorld.xz * 1.7 + vec2(time * 0.21, -time * 0.17));
    c = mix(vec3(0.002, 0.005, 0.025), vec3(0.008, 0.02, 0.085), ck);
    c = mix(c, mix(vec3(0.05, 0.028, 0.006), vec3(0.14, 0.08, 0.018), ck), lit);
    gl_FragColor = vec4(c, 0.97);
    return;
  }
  gl_FragColor = vec4(c, 0.86 - 0.04 * lit);
}`;

export class Stage {
  constructor(canvas) {
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: false, powerPreference: 'high-performance' });
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    this.renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
    this.scene = new THREE.Scene();
    this.scene.fog = new THREE.FogExp2(0x0a1226, 0.006);
    this.camera = new THREE.PerspectiveCamera(FOV, 1, 0.5, 900);
    this.post = new Post(this.renderer);
    this.time = 0;
    this.look = { focus: 30, range: 7, spread: 22, maxBlur: 7, tilt: 0.7, threshold: 0.7, bloom: 0.85, exposure: 1.2, vignette: 0.5, fade: 1 };
    // Camera rig: a target on the water and a distance; pitch fixed.
    this.rig = { target: new THREE.Vector3(), dist: 30, yaw: 0, pitch: PITCH, fov: FOV };
    this.goal = { target: new THREE.Vector3(), dist: 30, yaw: 0, pitch: PITCH, fov: FOV };
    this.shake = { trauma: 0, kick: new THREE.Vector2() };
    this.moonDir = new THREE.Vector3(0.35, 0.42, -1).normalize();

    const hemi = new THREE.HemisphereLight(0x5a6c9c, 0x10131f, 0.6);
    this.scene.add(hemi);
    this.hemi = hemi;
    this.moon = new THREE.DirectionalLight(0xc8d6ff, 1.2);
    this.moon.castShadow = true;
    this.moon.shadow.mapSize.set(2048, 2048);
    Object.assign(this.moon.shadow.camera, { left: -14, right: 14, top: 14, bottom: -14, near: 1, far: 120 });
    this.moon.shadow.bias = -0.0006;
    this.moon.shadow.normalBias = 0.02;
    this.scene.add(this.moon, this.moon.target);

    this.sky = new THREE.Mesh(new THREE.SphereGeometry(600, 32, 16), new THREE.ShaderMaterial({
      vertexShader: skyVS, fragmentShader: skyFS, side: THREE.BackSide, depthWrite: false,
      uniforms: { top: { value: new THREE.Color('#060a1c') }, horizon: { value: new THREE.Color('#1a2448') }, moonDir: { value: this.moonDir }, moonLight: { value: 0.5 }, phase: { value: 0.5 }, time: { value: 0 }, moonSize: { value: 1 } },
    }));
    this.sky.renderOrder = -10;
    this.scene.add(this.sky);
    this.sea = new THREE.Mesh(new THREE.PlaneGeometry(1400, 900).rotateX(-Math.PI / 2), new THREE.ShaderMaterial({
      vertexShader: seaVS, fragmentShader: seaFS,
      uniforms: { time: { value: 0 }, moonDir: { value: this.moonDir }, moonLight: { value: 0.5 }, camPos: { value: new THREE.Vector3() }, tide: { value: 0 }, clarity: { value: 0 }, kagee: { value: 0 } },
      transparent: true, depthWrite: true,
    }));
    // Before the pool surfaces (renderOrder 2): late in the night the rising sea
    // reaches pool level and must never paint over the pool being played.
    this.sea.renderOrder = 1;
    this.sea.position.set(40, -0.55, -300);
    this.scene.add(this.sea);
    this.lights = [];
    this.boatLights = new THREE.Group();
    this.scene.add(this.boatLights);
    this.resize();
    addEventListener('resize', () => this.resize());
  }

  setQuality(level) {
    this.quality = level;
    const cap = level === 'high' ? 1.75 : 1.15;
    this.renderer.setPixelRatio(Math.min(devicePixelRatio, cap));
    this.post.settings.dof = level === 'high';
    this.resize();
  }

  resize() {
    const w = innerWidth, h = innerHeight;
    this.renderer.setSize(w, h, false);
    const pr = this.renderer.getPixelRatio();
    this.post.setSize(Math.floor(w * pr), Math.floor(h * pr));
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    this.aspect = w / h;
  }

  // Night look from the real moon: illumination 0..1000.
  setMoon(light, lunarDay) {
    const k = light / 1000;
    this.moonLight = k;
    this.moon.intensity = 0.25 + 1.35 * k;
    this.moon.color.setHSL(0.62, 0.35 - k * 0.15, 0.78);
    this.hemi.intensity = 0.32 + 0.4 * k;
    this.sky.material.uniforms.moonLight.value = Math.max(k, 0.25);
    this.sky.material.uniforms.phase.value = ((lunarDay - 1) % 30) / 29.53;
    this.sea.material.uniforms.moonLight.value = 0.25 + k;
    // Sprites are lit by one colour: moonlight plus the sky, warmed a touch by lanterns.
    this.spriteLight = new THREE.Color(0.62, 0.68, 0.9).multiplyScalar(0.62 + 0.45 * k).add(new THREE.Color(0.12, 0.08, 0.04));
  }

  async loadWorld(url = '/models/tsukinoura-world.glb') {
    try {
      const gltf = await new GLTFLoader().loadAsync(url);
      this.world = gltf.scene;
      this.world.traverse(o => {
        if (o.isMesh) {
          o.castShadow = true; o.receiveShadow = true;
          const mats = Array.isArray(o.material) ? o.material : [o.material];
          for (const m of mats) {
            // Night stone and sand: no mirror highlights from the moon.
            if ('roughness' in m) { m.roughness = Math.max(m.roughness ?? 1, 0.72); m.metalness = 0; }
            for (const key of ['map', 'emissiveMap', 'roughnessMap', 'normalMap']) {
              if (m[key]) { m[key].magFilter = THREE.NearestFilter; m[key].minFilter = THREE.NearestMipmapNearestFilter; m[key].anisotropy = 4; }
            }
          }
        }
        const x = o.userData || {};
        if (o.name.startsWith('Light_') && x.color) this.lights.push({ pos: o.getWorldPosition(new THREE.Vector3()), color: new THREE.Color(x.color), intensity: x.intensity ?? 2, distance: x.distance ?? 8 });
        if (o.name.startsWith('Cam_')) (this.cams ||= {})[o.name] = { obj: o, fov: x.fov ?? 30 };
        if (o.parent?.name === 'Horizon_Boats') this.addBoatLight(o.getWorldPosition(new THREE.Vector3()));
        if (o.name === 'Moon_Dir' && x.dir) this.moonDir.set(...x.dir).normalize();
        if (o.name.startsWith('Cam_') && x.target) o.userData.target = x.target;
      });
      this.propKit = this.world.getObjectByName('PropKit');
      if (this.propKit) this.propKit.visible = false;
      this.scene.add(this.world);
      this.hasWorld = true;
    } catch (e) {
      console.info('world: using placeholder shelf', e.message);
      this.buildPlaceholderWorld();
    }
    // A pool of point lights reassigned to the lanterns nearest the camera target.
    this.pointPool = Array.from({ length: 6 }, () => {
      const l = new THREE.PointLight(0xffb35a, 0, 8, 1.6);
      this.scene.add(l); return l;
    });
    this.basinWater = {};
    for (const b of BASINS) {
      const shape = new THREE.Shape(b.outline.map(([x, y]) => new THREE.Vector2(x / 1024, -y / 1024)));
      const geo = new THREE.ShapeGeometry(shape).rotateX(-Math.PI / 2);
      const mat = new THREE.ShaderMaterial({ vertexShader: basinVS, fragmentShader: basinFS, transparent: true, depthWrite: false, uniforms: { time: { value: 0 }, lit: { value: 0 }, moon: { value: 0.5 }, kagee: { value: 0 } } });
      const m = new THREE.Mesh(geo, mat);
      m.position.set(b.centre[0] / 1024, -0.004, b.centre[1] / 1024);
      m.renderOrder = 2;
      this.scene.add(m);
      this.basinWater[b.id] = m;
    }
    this.moon.position.copy(this.moonDir).multiplyScalar(60);
    // The disc in the sky sits lower than the light so the title frame shows it.
    // Put it inside the title shot (upper right third); the sea glitter follows it.
    const cam = this.cams?.Cam_Title;
    if (cam) {
      const q = cam.obj.getWorldQuaternion(new THREE.Quaternion());
      const half = Math.tan(THREE.MathUtils.degToRad(cam.fov / 2));
      const d = new THREE.Vector3(0, 0, -1).applyQuaternion(q)
        .addScaledVector(new THREE.Vector3(0, 1, 0).applyQuaternion(q), half * 0.62)
        .addScaledVector(new THREE.Vector3(1, 0, 0).applyQuaternion(q), half * 0.55).normalize();
      this.skyMoon = d;
    } else {
      const flat = new THREE.Vector3(this.moonDir.x, 0, this.moonDir.z).normalize();
      this.skyMoon = flat.multiplyScalar(Math.cos(0.2)).setY(Math.sin(0.2)).normalize();
    }
    this.sky.material.uniforms.moonDir.value = this.skyMoon;
    this.sea.material.uniforms.moonDir.value = this.skyMoon;
  }

  addBoatLight(pos) {
    const m = new THREE.Mesh(new THREE.PlaneGeometry(0.7, 0.22), new THREE.MeshBasicMaterial({ color: new THREE.Color(1.6, 1.8, 1.5), fog: false }));
    m.position.copy(pos);
    m.lookAt(pos.x, pos.y, pos.z + 10);
    this.boatLights.add(m);
  }

  buildPlaceholderWorld() {
    const rock = new THREE.MeshStandardMaterial({ color: 0x2f3b56, roughness: 0.85, flatShading: true });
    const sand = new THREE.MeshStandardMaterial({ color: 0x5c6278, roughness: 1, side: THREE.DoubleSide });
    const g = new THREE.Group();
    const shelf = new THREE.Mesh(new THREE.BoxGeometry(130, 1, 30), rock);
    shelf.position.set(40, -0.85, -1);
    shelf.receiveShadow = true;
    g.add(shelf);
    for (const b of BASINS) {
      const cx = b.centre[0] / 1024, cz = b.centre[1] / 1024;
      const shape = new THREE.Shape(b.outline.map(([x, y]) => new THREE.Vector2(x / 1024, y / 1024)));
      const floor = new THREE.Mesh(new THREE.ShapeGeometry(shape).rotateX(Math.PI / 2), sand);
      floor.position.set(cx, -0.3, cz);
      floor.receiveShadow = true;
      g.add(floor);
      b.outline.forEach(([x, y], i) => {
        if (i % 1) return;
        const r = new THREE.Mesh(new THREE.IcosahedronGeometry(0.7 + (i % 3) * 0.2, 0), rock);
        const len = Math.hypot(x, y);
        r.position.set(cx + (x / 1024) * (1 + 0.75 / (len / 1024)), 0.1, cz + (y / 1024) * (1 + 0.75 / (len / 1024)));
        r.castShadow = r.receiveShadow = true;
        g.add(r);
      });
    }
    // Cut the shelf top where pools are: just lower a dark plate under each instead.
    this.scene.add(g);
    this.world = g;
    this.lights.push({ pos: new THREE.Vector3(-8, 2, 10), color: new THREE.Color('#ffb35a'), intensity: 3, distance: 12 });
    for (let i = 0; i < 10; i++) this.addBoatLight(new THREE.Vector3(-80 + i * 25, 0.6, -150 - (i % 3) * 20));
  }

  // Frame a pool: fit its outline on screen with room for the HUD.
  framePool(basin, { margin = 1.12, instant = false } = {}) {
    if (this.aspect < 0.8) margin = 1.04;
    const cx = basin.centre[0] / 1024, cz = basin.centre[1] / 1024;
    const pts = basin.outline.map(([x, y]) => new THREE.Vector3(cx + x / 1024, 0, cz + y / 1024));
    const goal = this.goal;
    goal.target.set(cx, 0, cz);
    goal.pitch = PITCH; goal.fov = FOV;
    // Search the distance that fits every outline point inside the view. On a
    // tall screen (3D look only) the camera may turn a quarter so the pool's long
    // side runs up the screen; whichever yaw gets closest wins.
    const cam = this.camera.clone();
    const portrait = this.aspect < 0.8;
    const yaws = portrait && this.turnable ? [0, Math.PI / 2, -Math.PI / 2] : [0];
    let best = { dist: Infinity, yaw: 0 };
    for (const yaw of yaws) {
      let lo = 8, hi = 120;
      for (let k = 0; k < 24; k++) {
        const mid = (lo + hi) / 2;
        this.placeCamera(cam, { target: goal.target, dist: mid, yaw, pitch: PITCH, fov: FOV });
        cam.updateMatrixWorld(); cam.updateProjectionMatrix();
        const [xr, yTop, yBot] = portrait ? [0.99, 0.78, -0.64] : [0.94, 0.8, -0.74];   // portrait: thin HUD strip on top, one-line prompt below
        const ok = pts.every(p => { const v = p.clone().project(cam); return Math.abs(v.x) < xr / margin && v.y < yTop / margin && v.y > yBot / margin; });
        if (ok) hi = mid; else lo = mid;
      }
      if (hi < best.dist * 0.97) best = { dist: hi, yaw };
    }
    goal.dist = best.dist; goal.yaw = best.yaw;
    // Nudge the target so the pool sits slightly above centre (aim bar at the bottom).
    if (instant) { this.rig.target.copy(goal.target); this.rig.dist = goal.dist; this.rig.yaw = goal.yaw; this.rig.pitch = PITCH; this.rig.fov = FOV; }
    this.focusPool = goal.target.clone();
  }

  placeCamera(cam, rig) {
    const dir = new THREE.Vector3(Math.sin(rig.yaw) * Math.cos(rig.pitch), Math.sin(rig.pitch), Math.cos(rig.yaw) * Math.cos(rig.pitch));
    cam.position.copy(rig.target).addScaledVector(dir, rig.dist);
    cam.fov = rig.fov; cam.aspect = this.aspect;
    cam.lookAt(rig.target);
  }

  addTrauma(v) { this.shake.trauma = Math.min(1, this.shake.trauma + v); }
  kick(x, y) { this.shake.kick.x += x; this.shake.kick.y += y; }

  // A timed, eased move of the rig (the side pan along the shelf between pools).
  panTo(seconds) {
    const r = this.rig;
    this.pan = { t: 0, dur: seconds, from: { target: r.target.clone(), dist: r.dist, yaw: r.yaw, pitch: r.pitch, fov: r.fov } };
  }

  update(dt, reducedMotion) {
    this.time += dt;
    const r = this.rig, g = this.goal;
    if (this.pan) {
      const p = this.pan;
      p.t += dt;
      const x = Math.min(1, p.t / p.dur), e = x * x * (3 - 2 * x);
      // Rise a little mid-pan so the move reads as travelling over the shelf.
      const lift = Math.sin(x * Math.PI) * 0.12 * p.from.dist;
      r.target.lerpVectors(p.from.target, g.target, e);
      r.dist = p.from.dist + (g.dist - p.from.dist) * e + lift;
      r.yaw = p.from.yaw + (g.yaw - p.from.yaw) * e;
      r.pitch = p.from.pitch + (g.pitch - p.from.pitch) * e;
      r.fov = p.from.fov + (g.fov - p.from.fov) * e;
      if (x >= 1) this.pan = null;
    } else {
      const k = 1 - Math.exp(-dt * (this.camSpeed || 3.2));
      r.target.lerp(g.target, k);
      r.dist += (g.dist - r.dist) * k;
      r.yaw += (g.yaw - r.yaw) * k;
      r.pitch += (g.pitch - r.pitch) * k;
      r.fov += (g.fov - r.fov) * k;
    }
    this.placeCamera(this.camera, r);
    // Shake (trauma^2 noise) and recoil kick, camera only.
    const s = this.shake;
    if (!reducedMotion && (s.trauma > 0 || s.kick.lengthSq() > 1e-6)) {
      const t = this.time * 31;
      const amt = s.trauma * s.trauma * 0.35;
      const right = new THREE.Vector3().setFromMatrixColumn(this.camera.matrixWorld, 0);
      const up = new THREE.Vector3().setFromMatrixColumn(this.camera.matrixWorld, 1);
      this.camera.position.addScaledVector(right, (Math.sin(t * 1.3) + Math.sin(t * 2.7) * 0.5) * amt + s.kick.x);
      this.camera.position.addScaledVector(up, (Math.sin(t * 1.7 + 2) + Math.sin(t * 3.1) * 0.5) * amt + s.kick.y);
    }
    s.trauma = Math.max(0, s.trauma - dt * 1.6);
    s.kick.multiplyScalar(Math.exp(-dt * 14));
    this.camera.updateProjectionMatrix();
    this.camera.updateMatrixWorld();

    // Moon shadow follows the camera target.
    this.moon.target.position.copy(r.target);
    this.moon.position.copy(r.target).addScaledVector(this.moonDir, 60);
    this.moon.target.updateMatrixWorld();
    // Nearest lanterns get the real point lights.
    const near = [...this.lights].sort((a, b) => a.pos.distanceToSquared(r.target) - b.pos.distanceToSquared(r.target)).slice(0, this.pointPool.length);
    this.pointPool.forEach((l, i) => {
      const src = near[i];
      if (!src) { l.intensity = 0; return; }
      l.position.copy(src.pos); l.color.copy(src.color); l.distance = src.distance;
      l.intensity = src.intensity * (0.92 + 0.08 * Math.sin(this.time * 7.3 + i * 1.7) * Math.sin(this.time * 3.1 + i));
    });
    // Tide level and dawn.
    if (this.tideGoal !== undefined) {
      const y = this.sea.position.y, d = this.tideGoal - y;
      this.sea.position.y = y + Math.sign(d) * Math.min(Math.abs(d), dt * (this.tideSpeed || 0.25));
    }
    if (this.dawn) {
      this.dawn = Math.min(1, this.dawn + dt / 9);
      const u = this.sky.material.uniforms;
      u.horizon.value.setRGB(0.10 + 0.55 * this.dawn, 0.14 + 0.32 * this.dawn, 0.28 + 0.12 * this.dawn);
      u.top.value.setRGB(0.024 + 0.08 * this.dawn, 0.04 + 0.1 * this.dawn, 0.11 + 0.16 * this.dawn);
      this.hemi.intensity = 0.4 + 0.5 * this.dawn;
    } else if (this.dawn === 0) {
      const u = this.sky.material.uniforms;
      u.horizon.value.set('#1a2448'); u.top.value.set('#060a1c');
      this.dawn = undefined;
    }
    this.sky.position.copy(this.camera.position);
    this.sky.material.uniforms.time.value = this.time;
    this.sea.material.uniforms.time.value = this.time;
    this.sea.material.uniforms.camPos.value.copy(this.camera.position);
    for (const m of Object.values(this.basinWater || {})) { m.material.uniforms.time.value = this.time; m.material.uniforms.moon.value = this.moonLight ?? 0.5; }
    this.look.focus = this.camera.position.distanceTo(r.target);
    // Focus dip: everything darkens for a moment while a pen's light floods in.
    this.focusDip = Math.max(0, (this.focusDip || 0) - dt * 1.6);
    this.fadeIn = Math.max(0, (this.fadeIn || 0) - dt * 2.2);
    this.look.exposure = 1.2 * (1 - this.focusDip);
    this.look.fade = 1 - Math.min(1, this.fadeIn) ** 2;
  }

  render() { this.post.render(this.scene, this.camera, this.look, this.time); }
}
